// Module Name for TSDocs
/**
 * [[include:fa-chargetime.md]]
 *
 * @module · ChargeTime Simulator
 */

import * as React from 'react';
import {FeatureAppEnvironment} from '@feature-hub/core';
import {
  ChargetimeFeatureAppContentConfig,
  CommonFeatureServiceDependencies,
  CommonFeatureServices,
  CommonOptionalFeatureServiceDependencies,
  CommonRequiredFeatureServiceDependencies,
  ComponentLibraryContext,
  ConfigurationsContextProvider,
  CustomOptionalFeatureServiceDependencies,
  EMobilityToolsFeatureAppComponentLibrary,
  EMobilityToolsFeatureAppTheme,
  ExternallyRenderedDisclaimerKind,
  FeatureAppEnvironmentContext,
  TrackingContextProvider,
  TrackingFunctions,
  WhiteLabelThemeProvider,
  wrapWithErrorBoundary,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {
  BffChargeTimeConfigurationResponse,
  DevConfig,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {App} from './app';
import {ChargeTimeSimulatorTrackingCtx} from './chargetime-tracking';

export {ChargeTimeSimulatorTrackingCtx};

// replace this with an interface once the config props are known
export type ChargeTimeSimulatorFeatureAppConfig = Record<string, never>;

// extend if there are custom dependencies for the charge app
export type ChargeTimeSimulatorFeatureAppDependencies =
  CommonFeatureServiceDependencies;

export {default as requiredExternals} from '@volkswagen-onehub/whitelabel-e-mobility-commons/externals.json';

export const requiredServices: {
  [key in keyof CommonRequiredFeatureServiceDependencies]: string;
} = {
  [CommonFeatureServices.layerManager]: '^1.0.0',
  [CommonFeatureServices.localeService]: '^1.0.0',
  [CommonFeatureServices.disclaimerManager]: '^1.0.0',
};

export const optionalServices: {
  [key in keyof CommonOptionalFeatureServiceDependencies]: string;
} = {
  [CommonFeatureServices.s2Logger]: '^1.0.0',
};

export const customOptionalServices: {
  [key in keyof Partial<CustomOptionalFeatureServiceDependencies>]: string;
} = {
  [CommonFeatureServices.layerButtonChanelService]: '^1.0.0',
};

export interface ChargeTimeSimulatorFeatureAppProps {
  componentsLibrary: EMobilityToolsFeatureAppComponentLibrary;
  theme: EMobilityToolsFeatureAppTheme;
  featureAppEnvironment: FeatureAppEnvironment<
    ChargeTimeSimulatorFeatureAppDependencies,
    ChargeTimeSimulatorFeatureAppConfig
  >;
  appDataPromise: Promise<BffChargeTimeConfigurationResponse>;
  appDataContent: ChargetimeFeatureAppContentConfig | undefined;
  trackingFunctions?: TrackingFunctions<ChargeTimeSimulatorTrackingCtx>;
  /**
   * By default, the Feature Apps register "externally rendered" disclaimers
   * as "footer references", which means they should show up in a footer that
   * subscribes to these disclaimers via the (GFA) disclaimer manager.
   *
   * This can be set to `module-reference` instead, if you want to render these
   * disclaimers differently (e.g. via a layer that opens)
   */
  externallyRenderedDisclaimerKind?: ExternallyRenderedDisclaimerKind;
  devConfig: DevConfig;
}

export function ChargeTimeSimulatorFeatureApp({
  theme,
  componentsLibrary,
  featureAppEnvironment,
  appDataPromise,
  appDataContent,
  trackingFunctions,
  externallyRenderedDisclaimerKind = 'footer-reference',
  devConfig,
}: ChargeTimeSimulatorFeatureAppProps) {
  const trackingCtxRef =
    React.useRef<ChargeTimeSimulatorTrackingCtx | null>(null);

  return (
    <TrackingContextProvider
      ctxRef={trackingCtxRef}
      trackingFunctions={trackingFunctions}
    >
      <WhiteLabelThemeProvider theme={theme}>
        <FeatureAppEnvironmentContext.Provider value={featureAppEnvironment}>
          <ConfigurationsContextProvider
            appDataPromise={appDataPromise}
            appDataContent={appDataContent}
            externallyRenderedDisclaimerKind={externallyRenderedDisclaimerKind}
            devConfig={devConfig}
          >
            <ComponentLibraryContext.Provider
              value={wrapWithErrorBoundary(componentsLibrary)}
            >
              <App />
            </ComponentLibraryContext.Provider>
          </ConfigurationsContextProvider>
        </FeatureAppEnvironmentContext.Provider>
      </WhiteLabelThemeProvider>
    </TrackingContextProvider>
  );
}
